import {
  useGetAllSettingsQuery,
  useAddSettingMutation,
  useGetSettingByIdQuery,
  useUpdateSettingMutation,
  useDeleteSettingMutation,
} from "../api/settingApi";
import { SettingResponseType } from "../types/settingResponse";
import { SettingPayloadType } from "../types/settingRequest";
import { getApiErrorMessage } from "../../../core/utils/errorHandler";
import { PaginationProps } from "../../../core/data/interface";

export const useSetting = (params?: PaginationProps) => {
  const { data, isLoading, isError, error } = useGetAllSettingsQuery(
    params || {}
  );

  const [
    addSettingMutation,
    { isLoading: isAdding, isError: isAddError, error: addError },
  ] = useAddSettingMutation();

  const [
    updateSettingMutation,
    { isLoading: isUpdating, isError: isUpdateError, error: updateError },
  ] = useUpdateSettingMutation();

  const [
    deleteSettingMutation,
    { isLoading: isDeleting, isError: isDeleteError, error: deleteError },
  ] = useDeleteSettingMutation();

  const errorMessage = isError ? getApiErrorMessage(error) : null;
  const addErrorMessage = isAddError ? getApiErrorMessage(addError) : null;
  const updateErrorMessage = isUpdateError
    ? getApiErrorMessage(updateError)
    : null;
  const deleteErrorMessage = isDeleteError
    ? getApiErrorMessage(deleteError)
    : null;
  const settings: SettingResponseType[] = data?.data?.items || [];

  const paginationData = {
    totalPages: data?.data?.totalPages,
    pageSize: data?.data?.pageSize,
    totalItems: data?.data?.totalItems,
    currentPage: data?.data?.currentPage,
  };

  const addNewSetting = async (payload: SettingPayloadType) => {
    try {
      const response = await addSettingMutation(payload).unwrap();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: addErrorMessage || "Failed to add setting",
      };
    }
  };

  const useGetSettingById = (id: string) => {
    const { data } = useGetSettingByIdQuery({ id }, { skip: !id });
    return data?.data.setting;
  };

  const updateSetting = async (id: string, payload: SettingPayloadType) => {
    try {
      const response = await updateSettingMutation({ id, payload }).unwrap();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: updateErrorMessage || "Failed to update setting",
      };
    }
  };

  const deleteSetting = async (id: string) => {
    try {
      const response = await deleteSettingMutation({ id }).unwrap();
      return response;
    } catch (error) {
      return error;
    }
  };

  const getSettingState = () => ({
    settings,
    isLoading,
    error: errorMessage,
  });

  return {
    getSettingState,
    paginationData,
    useGetSettingById,
    addNewSetting,
    updateSetting,
    deleteSetting,
    isAdding,
    isUpdating,
    isDeleting,
    addError: addErrorMessage,
    updateError: updateErrorMessage,
    deleteError: deleteErrorMessage,
  };
};
